import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";

import {
  changePasswordService,
  confirmPhoneVerificationService,
  deleteAccountService,
  getDeleteCheckService,
  getLoginHistoryService,
  getMyProfileService,
  getOrgProfileService,
  setPhoneLoginService,
  startPhoneVerificationService,
  updateMyProfileService,
  updateOrgProfileService,
} from "@/services/settings.service";
import { IAPIResponse } from "@/services/api.types";
import { authQueryKeys } from "@/hooks/useAuth";
import { AUTH_TOKEN_STORAGE_KEY } from "@/constant/auth.constant";
import {
  ChangePasswordPayload,
  ChangePasswordResult,
  LoginHistoryParams,
  LoginHistoryResponse,
  MyProfile,
  MyProfilePayload,
  OrgProfile,
  OrgProfilePayload,
  PhoneLoginTogglePayload,
  PhoneVerifyConfirmPayload,
  PhoneVerifyStartPayload,
  PhoneVerifyStartResult,
} from "@/types/settings.types";

export const settingsQueryKeys = {
  all: () => ["settings"] as const,
  myProfile: () => [...settingsQueryKeys.all(), "my-profile"] as const,
  orgProfile: (orgId: string) => [...settingsQueryKeys.all(), "org-profile", orgId] as const,
  loginHistory: (params: LoginHistoryParams) =>
    [...settingsQueryKeys.all(), "login-history", params] as const,
};

export const useGetMyProfile = () =>
  useQuery<MyProfile, Error>({
    queryKey: settingsQueryKeys.myProfile(),
    queryFn: getMyProfileService,
  });

export const usePatchMyProfile = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<MyProfile>, AxiosError, MyProfilePayload>({
    mutationFn: updateMyProfileService,
    onSuccess: res => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.myProfile() });
    },
    onError: err => toast.error(err.message),
  });
};

/** Saves straight away, outside the profile form. Refreshes only the auth user
 *  (header + Settings read the photo from it) — refetching the profile would
 *  reset the form and drop any unsaved edits. */
export const usePatchMyPhoto = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<MyProfile>, AxiosError, string>({
    mutationFn: photo_url => updateMyProfileService({ photo_url }),
    onSuccess: (_res, photo_url) => {
      toast.success(photo_url ? "Photo updated" : "Photo removed");
      queryClient.invalidateQueries({ queryKey: authQueryKeys.me() });
    },
    onError: err => toast.error(err.message),
  });
};

/** Sends the code. Not invalidating on success is deliberate: the profile has
 *  not changed yet — only `confirm` flips `phone_verified`. */
export const useStartPhoneVerification = () =>
  useMutation<IAPIResponse<PhoneVerifyStartResult>, AxiosError, PhoneVerifyStartPayload>({
    mutationFn: startPhoneVerificationService,
    retry: false,
    onSuccess: res => toast.success(res.message),
    onError: err => toast.error(err.message),
  });

export const useConfirmPhoneVerification = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<MyProfile>, AxiosError, PhoneVerifyConfirmPayload>({
    mutationFn: confirmPhoneVerificationService,
    retry: false,
    onSuccess: res => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.myProfile() });
    },
    onError: err => toast.error(err.message),
  });
};

export const useSetPhoneLogin = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<MyProfile>, AxiosError, PhoneLoginTogglePayload>({
    mutationFn: setPhoneLoginService,
    retry: false,
    onSuccess: res => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.myProfile() });
    },
    onError: err => toast.error(err.message),
  });
};

export const useGetOrgProfile =(orgId: string, { enabled = true }: { enabled?: boolean } = {}) =>
  useQuery<OrgProfile, Error>({
    queryKey: settingsQueryKeys.orgProfile(orgId),
    queryFn: () => getOrgProfileService(orgId),
    enabled: enabled && !!orgId,
  });

export const usePatchOrgProfile = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<OrgProfile>, AxiosError, { orgId: string; payload: OrgProfilePayload }>({
    mutationFn: ({ orgId, payload }) => updateOrgProfileService(orgId, payload),
    onSuccess: (res, { orgId }) => {
      toast.success(res.message);
      queryClient.invalidateQueries({ queryKey: settingsQueryKeys.orgProfile(orgId) });
    },
    onError: err => toast.error(err.message),
  });
};

export const useGetLoginHistory = (params: LoginHistoryParams = {}) =>
  useQuery<LoginHistoryResponse, Error>({
    queryKey: settingsQueryKeys.loginHistory(params),
    queryFn: () => getLoginHistoryService(params),
  });

/** Fetched fresh each time the delete dialog opens: blockers change as deals close. */
export const useGetDeleteCheck = (enabled: boolean) =>
  useQuery({
    queryKey: [...settingsQueryKeys.all(), "delete-check"],
    queryFn: getDeleteCheckService,
    enabled,
    gcTime: 0,
  });

/** The caller signs out on success: the server has already revoked every session. */
export const useDeleteAccount = () =>
  useMutation<IAPIResponse<{ ok: boolean }>, AxiosError, string>({
    mutationFn: deleteAccountService,
    onSuccess: res => toast.success(res.message),
    onError: err => toast.error(err.message),
  });

export const useChangePassword = () => {
  return useMutation<IAPIResponse<ChangePasswordResult>, AxiosError, ChangePasswordPayload>({
    mutationFn: changePasswordService,
    onSuccess: res => {
      // Changing a password revokes every token issued before this moment,
      // including the one this tab is holding. Swapping in the fresh token the
      // server returned is what stops the user being signed out of the very
      // tab they changed it in.
      const token = res.data?.token;
      if (token) localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
      toast.success(res.message);
    },
    onError: err => toast.error(err.message),
  });
};
